import { useMemo } from 'react';
import { ASSUMPTION_GAPS } from '../../data/conjointData';
import { getBaseChoiceRate } from '../../lib/calculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { formatNumber, formatCurrencyCompact } from '../../lib/formatters';
import { RiskBadge } from '../common/Badge';
import { InfoButton } from '../common/Tooltip';

interface Props {
  viewMode?: 'single' | 'portfolio';
}

export function AssumptionGapTable({ viewMode = 'single' }: Props) {
  const s1Leads = useSimulatorStore(s => s.assumptions.s1Leads);
  const packages = useSimulatorStore(s => s.packages);
  const portfolio = usePortfolioPnL();

  const rows = useMemo(() => {
    const isPortfolio = viewMode === 'portfolio';
    const dynamicS2Units = isPortfolio
      ? Math.round(portfolio.totalS2UnitsSplit)
      : Math.round(s1Leads * getBaseChoiceRate(1899));

    const s2Label = isPortfolio
      ? `~${formatNumber(dynamicS2Units)} across ${packages.length} packages (split)`
      : `~${formatNumber(dynamicS2Units)} at $1,899 (scenario)`;

    return ASSUMPTION_GAPS.map(gap => {
      if (gap.metric === 'S2 Monthly Units') {
        return { ...gap, conjointEvidence: s2Label };
      }
      return gap;
    });
  }, [s1Leads, viewMode, portfolio, packages.length]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Model Assumptions vs. Conjoint Context</h3>
        <InfoButton helpKey="assumptionGap" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">ProForma assumptions alongside conjoint observations. These are from different analytical frameworks and should not be directly compared.</p>

      {viewMode === 'portfolio' && (
        <div className="flex items-center gap-4 mb-4 px-3 py-2 rounded-lg border border-border/50" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="text-xs">
            <span className="text-muted">Portfolio P&L (split): </span>
            <span className={`font-mono font-semibold ${portfolio.portfolioPnLSplit >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}>
              {formatCurrencyCompact(portfolio.portfolioPnLSplit)}
            </span>
          </div>
          <div className="text-xs">
            <span className="text-muted">Total S2 Units: </span>
            <span className="font-mono font-semibold text-primary">{formatNumber(Math.round(portfolio.totalS2UnitsSplit))}</span>
          </div>
          <div className="text-xs">
            <span className="text-muted">Packages: </span>
            <span className="font-mono font-semibold text-primary">{packages.length}</span>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-secondary">
              <th className="text-left py-2 px-2 font-medium">Metric</th>
              <th className="text-left py-2 px-2 font-medium">ProForma</th>
              <th className="text-left py-2 px-2 font-medium">Conjoint Observation</th>
              <th className="text-left py-2 px-2 font-medium">Context</th>
              <th className="text-center py-2 px-2 font-medium"><span className="inline-flex items-center gap-1">Note <InfoButton helpKey="riskLevel" size={10} /></span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(gap => (
              <tr key={gap.metric} className="border-b border-border/50 transition-colors"
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                <td className="py-2.5 px-2 text-primary font-medium">{gap.metric}</td>
                <td className="py-2.5 px-2 text-muted font-mono text-xs">{gap.financialModel}</td>
                <td className="py-2.5 px-2 text-secondary font-mono text-xs">{gap.conjointEvidence}</td>
                <td className="py-2.5 px-2 text-muted text-xs">{gap.gap}</td>
                <td className="py-2.5 px-2 text-center"><RiskBadge level={gap.riskLevel} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
